declare var window: {
    appBaseUrl: string;
    designSetUrlRoot: string;
    RFE: any;
    cacheBuster: string;
};

let callbackSequence = 0;

/**
 * Helper method to unify Ajax requests in the loader method
 * @param method - request method (GET, POST, PUT, ...)
 * @param url - requested URL
 * @returns {Promise}
 * @deprecated because this way of loading scripts prevents debugging (win10 + chrome dev. tools)
 */
function makeRequest(method: string, url: string): Promise<any> {
    return new Promise((resolve: any, reject: any): void => {
        const xhr = new XMLHttpRequest();
        xhr.open(method, url);
        xhr.onload = function (): void {
            if (this.status >= 200 && this.status < 300) {
                resolve(xhr.response);
            }
            else {
                reject({
                    status: this.status,
                    statusText: xhr.statusText
                });
            }
        };
        xhr.onerror = function () {
            reject({
                status: this.status,
                statusText: xhr.statusText
            });
        };
        xhr.send();
    });
}

/**
 * Add the module to the page and evaluate it
 * @param moduleName - filename of the module where the code is located
 * @param moduleCode - Source code of the module
 */
function evalModule(moduleName: string, moduleCode: string) {
    const src = `//# sourceURL=${moduleName}.js\n\n${moduleCode}`;
    const scriptCont = document.getElementById('scriptLoaderTarget');
    // IE9 - needs a different approach
    if (/MSIE\s/.test(navigator.userAgent) && parseFloat(navigator.appVersion.split('MSIE')[1]) < 10) {
        /* tslint:disable-next-line no-eval */
        eval(src);
    }
    else {
        const element = document.createElement('script');
        element.type = 'text/javascript';
        element.innerHTML = src;
        scriptCont && scriptCont.appendChild(element);
    }
}

/**
 * Load all the listed modules from the server in the correct order and add the source codes to the page
 * The funcion has 2 different strategies for loading modules depending on whether the loader is run
 * in the development or production mode
 * @param moduleNames - Array of module names to be loaded
 */
function loadModulesByNames(moduleNames: Array<string>): any {
    const scriptCont = document.getElementById('scriptLoaderTarget');
    if (process.env.NODE_ENV === 'production') {
        const callbackName = `_resourceLoaderReceiver_${callbackSequence++}`;
        const script = document.createElement('SCRIPT') as HTMLScriptElement;
        script.src = `${window.appBaseUrl}res?callback=${callbackName}${window.cacheBuster ? "&x=" + window.cacheBuster : ""}&dependencies=${moduleNames.join(',')}`;
        const result = new Promise((resolve, reject) => {
            const cleanup = () => {
                delete window[callbackName];
            };
            const err = (e: any) => {
                cleanup();
                reject(e);
            };
            window[callbackName] = () => {
                cleanup();
                resolve();
            };
            script.onerror = err;
            script.onabort = err;
        });
        scriptCont && scriptCont.appendChild(script);
        return result;
    }
    else {
        if (process.env.NODE_ENV === 'patternlab') {
            console.info(`Loading modules: ${moduleNames.join(', ')}`);
        }
        const urlTemplate = window.designSetUrlRoot ?
            `${window.designSetUrlRoot}/js/modules/{moduleName}.js` :
            `${window.appBaseUrl}Scripts/responsive/modules/{moduleName}.js`;
        let p: Promise<any> = new Promise((resolve) => resolve());
        for (const moduleName of moduleNames) {
            p = p
                .then(() => {
                    return new Promise((resolve: any, reject: any): void => {
                        const script = document.createElement('SCRIPT') as HTMLScriptElement;
                        script.type = 'text/javascript';
                        script.src = urlTemplate.replace('{moduleName}', moduleName);
                        script.onload = resolve;
                        script.onerror = (e) => reject(e);
                        scriptCont && scriptCont.appendChild(script);
                    });
                })
                .catch((e: any) => {
                    console.error(e);
                });
        }
        return p;
    }
}

/**
 * Tries to find the loaded module by name
 * the module specified by the moduleName must be already included in the page
 * @param moduleName - module to be found
 */
function getLoadedModuleByName(moduleName: string) {
    if (typeof (window as any)['_RFE_module_' + moduleName] !== 'undefined') {
        return (window as any)['_RFE_module_' + moduleName];
    }
    return undefined;
}

/**
 * Tries to find the loaded modules by name
 * the modules must be already loaded and included in the page
 * @param moduleNames - Array of module names to be loaded
 * @return list of requested modules
 */
function getLoadedModulesByNames(moduleNames: Array<string>): { [moduleName: string]: any } {
    const result: { [key: string]: any } = {};
    for (const moduleName of moduleNames) {
        const loadedModule = getLoadedModuleByName(moduleName);
        if (typeof loadedModule === 'undefined') {
            throw new Error('Module "' + moduleName + '" not found');
        }
        result[moduleName] = loadedModule;
    }
    return result;
}

/**
 * Determines, which modules are missing (are not loaded in the page)
 * @param moduleNames - Array of module names
 * @returns Array of module names that are not included in the page
 */
function getMissingModuleNames(moduleNames: Array<string>): Array<string> {
    const result: Array<string> = [];
    for (const moduleName of moduleNames) {
        if (typeof getLoadedModuleByName(moduleName) === 'undefined') {
            result.push(moduleName);
        }
    }
    return result;
}

/**
 * Returns all the modules specified by its names
 * The function load the modules that are missing in the page and then returns all the required modules
 * @param moduleNames - Array of module names
 * @returns {Promise<Array<any>>} - Promise of the array of the modules
 */
export const requireAll = async (moduleNames: Array<string>) => {
    const missingModuleNames = getMissingModuleNames(moduleNames);

    if (missingModuleNames.length === 0) {
        return getLoadedModulesByNames(moduleNames);
    }

    await loadModulesByNames(missingModuleNames);
    return getLoadedModulesByNames(moduleNames);
};

/**
 * Load and return one module
 * @param moduleName - module name
 * @return module by name
 */
export const require = async (moduleName: string) => {
    const loadedModule = getLoadedModuleByName(moduleName);
    if (typeof loadedModule === 'undefined') {
        await loadModulesByNames([moduleName]);
        return getLoadedModuleByName(moduleName);
    }
    return loadedModule;
};

/**
 * Set the important methods to the passed variable so the methods are globally available
 * @param RFE - global variable to assign important methods to
 */
const initRfeModuleLoader = (RFE: any) => {
    RFE.requireAll = requireAll;
    RFE.require = require;
};

window.RFE = window.RFE || {};
initRfeModuleLoader(window.RFE);



// WEBPACK FOOTER //
// ./node_modules/source-map-loader!./source/js/helpers/loader.ts